import { priceZodSchema } from "@@/shared/prices/price";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { generateWordSlug } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    _OrderEventBaseFactoryParams} from "./_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "./_order-base.event";

const orderPartialRefundRequestedEventZodSchema =
    _orderEventBaseZodObject.merge(
        z.object({
            type: z.literal("PARTIAL_REFUND_REQUESTED"),
            reason: translatableZodSchema,
            requestCreatedBy: userIdZodSchema.or(systemUserZodSchema),
            amount: priceZodSchema,
        }),
    );
type OrderPartialRefundRequestedEvent = z.infer<
    typeof orderPartialRefundRequestedEventZodSchema
>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPartialRefundRequestedEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;

const orderPartialRefundRequestedEventFactory = (
    params: Params,
): OrderPartialRefundRequestedEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.orderId,
        orderGroupId: params.orderGroupId,
        _version: params._version,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        signature: [
            params.orderId,
            "PARTIAL_REFUND_REQUESTED",
            [generateWordSlug()],
        ],
    }),
    type: "PARTIAL_REFUND_REQUESTED",
    reason: params.reason,
    requestCreatedBy: params.requestCreatedBy,
    amount: params.amount,
});

const isOrderPartialRefundRequestedEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPartialRefundRequestedEvent => {
    return (
        event._type === "ORDER_EVENT" &&
        event.type === "PARTIAL_REFUND_REQUESTED"
    );
};

export {
    isOrderPartialRefundRequestedEvent,
    orderPartialRefundRequestedEventFactory,
    orderPartialRefundRequestedEventZodSchema,
};
export type { OrderPartialRefundRequestedEvent };
